import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, $getRoot, $isTextNode, ParagraphNode, TextNode } from 'lexical';
import { useEffect } from 'react';

const RED = 'color:#dc2626'; // key
const GREEN = 'color:#006c3a'; // value

type ChildInfo = {
  key: string;
  isText: boolean;
  length: number;
};

function findKeyAndColon(line: string) {
  let inStr = false,
    esc = false;

  let qStart = -1,
    qEnd = -1,
    colon = -1;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inStr) {
      if (esc) {
        esc = false;
        continue;
      }
      if (ch === '\\') {
        esc = true;
        continue;
      }
      if (ch === '"') {
        inStr = false;
        qEnd = i;
      }
      continue;
    } else {
      if (ch === '"') {
        inStr = true;
        if (qStart === -1) qStart = i;
        continue;
      }
      if (ch === ':' && qStart !== -1 && qEnd !== -1) {
        colon = i;
        break;
      }
    }
  }

  if (qStart !== -1 && qEnd !== -1 && colon !== -1) {
    let valueStart = colon + 1;
    while (valueStart < line.length && /\s/.test(line[valueStart])) valueStart++;
    return { qStart, qEnd, colon, valueStart };
  }

  let valueStart = 0;
  while (valueStart < line.length && /\s/.test(line[valueStart])) valueStart++;
  return { qStart: -1, qEnd: -1, colon: -1, valueStart };
}

export default function JsonKeyValueColorPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        const plans: Array<() => void> = [];

        editorState.read(() => {
          const root = $getRoot();
          const paragraphs = root
            .getChildren()
            .filter((n) => n.getType() === 'paragraph') as ParagraphNode[];

          for (const p of paragraphs) {
            const children = p.getChildren();
            if (children.length === 0) continue;

            const childInfos: ChildInfo[] = [];
            let line = '';
            for (const ch of children) {
              if ($isTextNode(ch)) {
                const t = ch.getTextContent();
                line += t;
                childInfos.push({ key: ch.getKey(), isText: true, length: t.length });
              } else {
                line += '\uFFFC';
                childInfos.push({ key: ch.getKey(), isText: false, length: 1 });
              }
            }

            const { qStart, qEnd, valueStart } = findKeyAndColon(line);

            const keyFrom = qStart >= 0 ? qStart : -1;
            const keyTo = qEnd >= 0 ? qEnd + 1 : -1;
            const valFrom = valueStart;

            let cursor = 0;
            for (const info of childInfos) {
              const start = cursor;
              const end = cursor + info.length;
              cursor = end;

              const coversKey = keyFrom >= 0 && keyTo >= 0 && !(end <= keyFrom || start >= keyTo);
              const coversVal = !(end <= valFrom || start >= line.length);

              if (info.isText) {
                plans.push(() => {
                  const node = $getNodeByKey(info.key) as TextNode | null;
                  if (!node || !$isTextNode(node)) return;

                  const text = node.getTextContent();
                  const nodeLen = text.length;

                  const localKeyStart = keyFrom >= 0 ? Math.max(0, keyFrom - start) : -1;
                  const localKeyEnd = keyTo >= 0 ? Math.min(nodeLen, keyTo - start) : -1;
                  const localValStart = Math.max(0, valFrom - start);

                  if (coversKey && !coversVal) {
                    if (node.getStyle() !== RED) node.setStyle(RED);
                    return;
                  }

                  if (!coversKey && coversVal) {
                    if (node.getStyle() !== GREEN) node.setStyle(GREEN);
                    return;
                  }

                  if (!coversKey && !coversVal) {
                    if (node.getStyle() !== '') node.setStyle('');
                    return;
                  }

                  const cuts: number[] = [];
                  if (coversKey) {
                    if (localKeyStart > 0 && localKeyStart < nodeLen) cuts.push(localKeyStart);
                    if (localKeyEnd > 0 && localKeyEnd < nodeLen) cuts.push(localKeyEnd);
                  }
                  if (coversVal) {
                    if (localValStart > 0 && localValStart < nodeLen) cuts.push(localValStart);
                  }

                  const offsets = Array.from(new Set(cuts)).sort((a, b) => a - b);
                  if (offsets.length === 0) {
                    node.setStyle(coversVal ? GREEN : coversKey ? RED : '');
                    return;
                  }

                  const original = text;
                  const segments: string[] = [];
                  let prev = 0;
                  for (const off of offsets) {
                    segments.push(original.slice(prev, off));
                    prev = off;
                  }
                  segments.push(original.slice(prev));

                  node.setTextContent(segments[0]);
                  const seg0From = start;
                  const seg0To = start + segments[0].length;
                  const seg0IsKey =
                    keyFrom >= 0 && keyTo >= 0 && !(seg0To <= keyFrom || seg0From >= keyTo);
                  const seg0IsVal = !(seg0To <= valFrom || seg0From >= line.length);
                  node.setStyle(seg0IsVal ? GREEN : seg0IsKey ? RED : '');

                  let ref: TextNode = node;
                  for (let i = 1; i < segments.length; i++) {
                    const segText = segments[i];
                    const newNode = new TextNode(segText);
                    const segFrom = start + segments.slice(0, i).reduce((s, t) => s + t.length, 0);
                    const segTo = segFrom + segText.length;
                    const isKey =
                      keyFrom >= 0 && keyTo >= 0 && !(segTo <= keyFrom || segFrom >= keyTo);
                    const isVal = !(segTo <= valFrom || segFrom >= line.length);
                    newNode.setStyle(isVal ? GREEN : isKey ? RED : '');
                    ref.insertAfter(newNode);
                    ref = newNode;
                  }
                });
              } else {
                plans.push(() => {
                  const elem = editor.getElementByKey(info.key);
                  if (!elem) return;
                  const isVal = !(end <= valFrom || start >= line.length);
                  if (isVal) {
                    elem.classList.add('json-value');
                    (elem as HTMLElement).style.color = '#1d4ed8';
                  } else {
                    elem.classList.remove('json-value');
                    (elem as HTMLElement).style.color = '';
                  }
                });
              }
            }
          }
        });

        if (plans.length) {
          editor.update(() => {
            for (const fn of plans) fn();
          });
        }
      }),
    [editor],
  );

  return null;
}
